import { useState } from 'react';
import { Button, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { orderService } from '@/services/order.service';
import { exportReport, formatNumberCell, formatDateCell } from '@/utils/exportReport';
import type { TenantOrder } from '@/mocks/orders';
import type { OrderListQuery } from '@/services/queryKeys';

interface ExportOrderButtonProps {
    filter: Omit<OrderListQuery, 'page' | 'page_size'>;
}

const STATE_TEXT: Record<string, string> = {
    DRAFT: 'Nháp',
    PENDING: 'Chờ xử lý',
    WAIT_PAYMENT: 'Chờ thanh toán',
    PAID: 'Đã thanh toán',
    PARTIAL_PAID: 'Thanh toán một phần',
    CONFIRMED: 'Đã xác nhận',
    DELIVERING: 'Đang giao',
    COMPLETED: 'Hoàn thành',
    CANCELED: 'Đã huỷ',
    RETURNED: 'Đã hoàn trả',
};

const PAYMENT_TEXT: Record<string, string> = {
    cash: 'Tiền mặt',
    transfer: 'Chuyển khoản',
    card: 'Thẻ',
    mix: 'Hỗn hợp',
};

export function ExportOrderButton({ filter }: ExportOrderButtonProps) {
    const [loading, setLoading] = useState(false);

    const handleExport = async () => {
        setLoading(true);
        try {
            const { items } = await orderService.exportList(filter);

            const rows = items.map((order: TenantOrder, idx: number) => {
                const totalQty = order.items?.reduce((sum, it) => sum + (it.qty ?? 0), 0) ?? 0;
                return {
                    stt: idx + 1,
                    order_number: order.order_number,
                    customer_name: order.customer?.name ?? '',
                    customer_phone: order.customer?.phone ?? '',
                    total_qty: totalQty,
                    payment_method: order.payment_method ? (PAYMENT_TEXT[order.payment_method] ?? order.payment_method) : '',
                    state: STATE_TEXT[order.state] ?? order.state,
                    cashier_name: order.cashier?.name ?? '',
                    created_at: order.created_at,
                    total_amount: order.total_amount,
                };
            });

            exportReport({
                filename: `don-hang-${new Date().toISOString().slice(0, 10)}`,
                format: 'excel',
                title: 'Danh sách đơn hàng',
                sheets: [
                    {
                        name: 'Đơn hàng',
                        columns: [
                            { key: 'stt', header: 'STT' },
                            { key: 'order_number', header: 'Mã đơn' },
                            { key: 'customer_name', header: 'Khách hàng' },
                            { key: 'customer_phone', header: 'SĐT' },
                            { key: 'total_qty', header: 'Số lượng sản phẩm', format: formatNumberCell },
                            { key: 'payment_method', header: 'Hình thức thanh toán' },
                            { key: 'state', header: 'Trạng thái' },
                            { key: 'cashier_name', header: 'Nhân viên' },
                            { key: 'created_at', header: 'Thời gian tạo đơn', format: formatDateCell },
                            { key: 'total_amount', header: 'Tổng tiền', format: formatNumberCell },
                        ],
                        rows,
                    },
                ],
            });

            void message.success(`Xuất thành công ${items.length} đơn hàng`);
        } catch {
            void message.error('Xuất Excel thất bại');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Button
            size="small"
            style={{ marginBottom: 8 }}
            icon={<DownloadOutlined />}
            loading={loading}
            onClick={() => void handleExport()}
        >
            Xuất Excel
        </Button>
    );
}
